/**
 * Maps the document-IR form model (`describeCompositionForm`) onto the
 * legacy editor's field vocabulary, so the beta editor reuses the same
 * inputs (date mask, option selects, money, data tables) as the template
 * lane. Pure — no React, no network.
 *
 * The beta and legacy lanes share one `contract.values` bag per template
 * version: a select may hold either the ADR-0062 option code (what the beta
 * editor writes) or the legacy option value (what template defaults and
 * older edits hold). Blocks accept both; the editor displays the code.
 */

import {
  CUSTOM_FIELDS_GROUP_ID,
  CUSTOM_FIELDS_GROUP_TITLE,
  autofillMode,
  deriveCustomFields,
  fromLegacyAutofill,
  type ActionLog,
  type CompositionForm,
  type FormGroup,
  type LegacyAutofillSource,
  type FormField,
  type PartySlotDecl,
  type PropertySlotDecl,
  type SlotType,
} from '@hyranai/regta-document-ir';
import type {
  DataTableColumn,
  FieldType,
  PartySlot,
  PropertySlot,
  VariableField,
} from '@hyranai/regta-templates';

export type EditorField = VariableField<Record<string, unknown>>;

const DIRECT_TYPES: Partial<Record<SlotType, FieldType>> = {
  text: 'text',
  textarea: 'textarea',
  number: 'number',
  currency: 'currency',
  date: 'date',
  boolean: 'boolean',
  select: 'select',
  multi_select: 'multi_select',
  select_with_freetext: 'select_with_freetext',
  attachment: 'attachment_ref',
};

const LEGACY_TABLE_TYPES: ReadonlySet<FieldType> = new Set([
  'data_table',
  'area_list',
  'rent_schedule',
]);

const COLUMN_KIND: Partial<Record<SlotType, DataTableColumn['kind']>> = {
  text: 'text',
  textarea: 'text',
  date: 'text',
  number: 'number',
  currency: 'currency',
};

/** Slot types the parties / property panels own; never rendered as inputs. */
export const PANEL_SLOT_TYPES: ReadonlySet<SlotType> = new Set(['party', 'property']);

/**
 * The legacy input for an IR form field, or `null` when the editor has no
 * input for it (party/property slots live in their panels; a structured
 * list with no row shape has no generic editor).
 *
 * Tables reuse the legacy template's own field when it declares one for the
 * same key (same value shape — the lanes share values), otherwise the
 * IR row columns become a plain `data_table`.
 */
export function toEditorField(field: FormField, legacy?: EditorField): EditorField | null {
  if (PANEL_SLOT_TYPES.has(field.type)) return null;
  const required = field.required || field.statutory;
  const base = {
    key: field.key,
    label: field.label,
    section: field.groupId,
    requiredAtFinal: required,
    ...(field.help ? { help: field.help } : {}),
  };

  if (field.type === 'table' || field.type === 'list') {
    if (legacy && LEGACY_TABLE_TYPES.has(legacy.type)) {
      return { ...legacy, ...base, ...(legacy.help && !field.help ? { help: legacy.help } : {}) };
    }
    const columns = field.columns ?? [];
    if (columns.length === 0 || columns.some((c) => PANEL_SLOT_TYPES.has(c.type))) return null;
    return {
      ...base,
      type: 'data_table',
      xlsxImport: false,
      columns: columns.map((c) => ({
        key: c.key,
        label: c.label,
        kind: COLUMN_KIND[c.type] ?? 'text',
      })),
    };
  }

  if (field.type === 'attachment' && field.repeats) return null;
  const type = DIRECT_TYPES[field.type];
  if (!type) return null;

  return {
    ...base,
    type,
    ...(field.options
      ? {
          options: field.options.map((o) => ({
            value: o.code,
            label: o.label,
            code: o.code,
            ...(o.summary ? { summary: o.summary } : {}),
          })),
        }
      : {}),
    ...(legacy?.type === 'attachment_ref' && legacy.attachmentDefaultName
      ? { attachmentDefaultName: legacy.attachmentDefaultName }
      : {}),
  };
}

/**
 * Adds the legacy template's autofill binding to every field the blocks do
 * not bind themselves — the server resolves autofill the same way (IR
 * binding first, legacy as fallback), so the editor shows the same set of
 * auto/manual fields the compiler fills.
 */
export function withLegacyAutofill(
  form: CompositionForm,
  legacyFields: ReadonlyMap<string, EditorField>,
): CompositionForm {
  const enriched = new Map<string, FormField>();
  for (const field of form.fields) {
    const legacy = legacyFields.get(field.key)?.autofill;
    enriched.set(
      field.key,
      field.autofill || !legacy
        ? field
        : { ...field, autofill: fromLegacyAutofill(legacy as LegacyAutofillSource) },
    );
  }
  return {
    ...form,
    fields: form.fields.map((f) => enriched.get(f.key)!),
    groups: form.groups.map((g) => ({ ...g, fields: g.fields.map((f) => enriched.get(f.key)!) })),
  };
}

/**
 * Adds the own fields the action log defines (ADR-0102 addendum) as the
 * last group, "Egna uppgifter" — the same group the adaptation session
 * builds. Their values live in `contract.values` under `custom_…` keys and
 * save like any other field. No log or no own fields → the form unchanged.
 */
export function withCustomFields(form: CompositionForm, log: ActionLog | null | undefined): CompositionForm {
  const fields = log ? deriveCustomFields(log) : [];
  if (fields.length === 0) return form;
  const group: FormGroup = {
    instanceId: CUSTOM_FIELDS_GROUP_ID,
    blockRef: '',
    blockKey: '',
    title: CUSTOM_FIELDS_GROUP_TITLE,
    summary: '',
    part: form.groups[0]?.part ?? 'main',
    placement: 'body',
    fields,
    sharedKeys: [],
    fixed: {},
    custom: true,
  };
  return { ...form, groups: [...form.groups, group], fields: [...form.fields, ...fields] };
}

/** `custom_values_invalid` from save_draft as Swedish text, naming the fields. */
export function customValuesErrorText(
  data: Record<string, unknown> | undefined,
  labelOf: (key: string) => string | null,
): string {
  const errors = Array.isArray(data?.errors) ? (data!.errors as Array<{ key?: unknown; reason?: unknown }>) : [];
  const names = errors
    .map((e) => (typeof e.key === 'string' ? (labelOf(e.key) ?? e.key) : null))
    .filter((n): n is string => n != null);
  if (errors.some((e) => e.reason === 'custom_fields_require_ir')) {
    return 'Egna uppgifter finns bara på avtal skapade med den nya dokumentmotorn.';
  }
  if (errors.length > 0 && errors.every((e) => e.reason === 'custom_value_unknown')) {
    return `Fältet finns inte längre${names.length ? `: ${names.join(', ')}` : ''}. Ladda om sidan.`;
  }
  return `Egna uppgifter har fel format${names.length ? ` (${names.join(', ')})` : ''}: datum skrivs ÅÅÅÅ-MM-DD, tal och belopp med siffror.`;
}

const toCode = (field: FormField, legacy: EditorField | undefined, raw: unknown): unknown => {
  if (typeof raw !== 'string' || !field.options) return raw;
  if (field.options.some((o) => o.code === raw)) return raw;
  const viaLegacy = legacy?.options?.find((o) => o.value === raw)?.code;
  if (viaLegacy && field.options.some((o) => o.code === viaLegacy)) return viaLegacy;
  const viaLabel = field.options.find((o) => o.label === raw)?.code;
  return viaLabel ?? raw;
};

/** The value as the beta inputs show it: option values normalised to codes. */
export function displayValue(field: FormField, value: unknown, legacy?: EditorField): unknown {
  if (field.type === 'multi_select' && Array.isArray(value)) {
    return value.map((v) => toCode(field, legacy, v));
  }
  if (field.type === 'select' || field.type === 'select_with_freetext') {
    return toCode(field, legacy, value);
  }
  return value;
}

/**
 * The values the form evaluates visibility and completeness against: the
 * composition defaults, the editor's values, and — for autofill fields in
 * auto mode — the value the server resolved from parties/properties.
 */
export function effectiveValues(
  form: Pick<CompositionForm, 'defaults'> | null,
  values: Record<string, unknown>,
  autoValues: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const scope: Record<string, unknown> = { ...(form?.defaults ?? {}), ...values };
  for (const [key, value] of Object.entries(autoValues ?? {})) {
    if (autofillMode(values, key) === 'auto') scope[key] = value;
  }
  return scope;
}

/**
 * The editor's values as the legacy template version reads them: every
 * select answer stored as an ADR-0062 option code is mapped to the legacy
 * option value (ADR-0099). Used where legacy metadata rules (slot
 * visibility, bilaga parent overlay) evaluate a beta contract's values.
 */
export function toLegacyValues(
  legacyFields: ReadonlyMap<string, EditorField>,
  values: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...values };
  const map = (field: EditorField, raw: unknown): unknown => {
    if (typeof raw !== 'string' || !field.options) return raw;
    const opt = field.options.find((o) => (o.code ?? o.value) === raw);
    return opt ? opt.value : raw;
  };
  for (const [key, field] of legacyFields) {
    if (!field.options?.length || !(key in values)) continue;
    const raw = values[key];
    out[key] = Array.isArray(raw) ? raw.map((r) => map(field, r)) : map(field, raw);
  }
  return out;
}

const PARTY_ROLES: ReadonlySet<PartySlot['role']> = new Set([
  'lessor',
  'lessee',
  'guarantor',
  'witness',
  'other',
]);

/** IR party-slot declarations in the shape `PartiesPanel` renders. */
export function toPanelPartySlots(slots: readonly PartySlotDecl[]): PartySlot[] {
  return slots.map((s) => ({
    id: s.id,
    label: s.label,
    role: PARTY_ROLES.has(s.role as PartySlot['role']) ? (s.role as PartySlot['role']) : 'other',
    ordinal: s.ordinal ?? 1,
    ...(s.required !== undefined ? { required: s.required } : {}),
    ...(s.signs !== undefined ? { signs: s.signs } : {}),
    ...(s.help ? { help: s.help } : {}),
  }));
}

export type PanelPropertySlot = { label: string; multi: boolean; required?: boolean; help?: string };

/** The property panel to show: the IR declaration, else the legacy one. */
export function panelPropertySlot(
  irSlots: readonly PropertySlotDecl[],
  legacySlots: ReadonlyArray<PropertySlot> | undefined,
  hasPropertyAutofill: boolean,
): PanelPropertySlot | null {
  const ir = irSlots[0];
  if (ir) {
    return {
      label: ir.label,
      multi: ir.multi === true,
      ...(ir.required !== undefined ? { required: ir.required } : {}),
      ...(ir.help ? { help: ir.help } : {}),
    };
  }
  const legacy = legacySlots?.[0];
  if (legacy) {
    return {
      label: legacy.label,
      multi: legacy.multi === true,
      ...(legacy.required !== undefined ? { required: legacy.required } : {}),
      ...(legacy.help ? { help: legacy.help } : {}),
    };
  }
  return hasPropertyAutofill ? { label: 'Fastighet', multi: false } : null;
}

/** Copy shown beside an autofilled field, by where its value comes from. */
export function autofillSourceLabel(
  field: FormField,
  partySlots: readonly PartySlotDecl[],
): string {
  const af = field.autofill;
  if (!af) return '';
  if (af.kind === 'party') {
    const slot = partySlots.find((s) => s.role === af.role && (s.ordinal ?? 1) === (af.ordinal ?? 1));
    return `Hämtas automatiskt från ${slot ? slot.label.toLowerCase() : 'parten'}.`;
  }
  if (af.kind === 'property_list') return 'Hämtas automatiskt från länkade fastigheter.';
  return 'Hämtas automatiskt från länkad fastighet.';
}

/**
 * The preview iframe document. Fragments come from the server renderer
 * (already escaped); `data-instance-id` / `data-ref` attributes are kept as
 * emitted — later fine-tune editing addresses blocks and sections by them.
 */
export function previewSrcDoc(
  parts: ReadonlyArray<{ key: string; title: string; html: string; letterheadHtml: string; footerHtml: string }>,
  css: string,
  extraCss: string,
): string {
  const pages = parts
    .map(
      (p, i) =>
        `${parts.length > 1 ? `<div class="bp-part-label">Dokument ${i + 1} / ${parts.length} · ${escapeHtml(p.title)}</div>` : ''}` +
        `<section class="rd-page" data-part="${escapeHtml(p.key)}">` +
        (p.letterheadHtml ? `<div class="rd-region rd-region--letterhead">${p.letterheadHtml}</div>` : '') +
        p.html +
        (p.footerHtml ? `<div class="rd-region rd-region--footer">${p.footerHtml}</div>` : '') +
        `</section>`,
    )
    .join('');
  return (
    '<!doctype html><html lang="sv"><head><meta charset="utf-8">' +
    `<style>${css}\n${extraCss}\n` +
    'html,body{margin:0;background:#f5f5f4}body{padding:16px}' +
    '.bp-part-label{max-width:21cm;margin:0 auto 6px;font:500 11px/1.4 system-ui,sans-serif;color:#57534e;text-transform:uppercase;letter-spacing:.04em}' +
    '.rd-page.bp-flash,[data-instance-id].bp-flash{outline:2px solid #a5b4fc;outline-offset:4px;transition:outline-color .6s}' +
    `</style></head><body>${pages}</body></html>`
  );
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}
